import React, { useEffect, useMemo } from "react";

import {
    asString,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { createTextValidator } from "../validation/defaultValidators";

export interface TextAreaFieldProps
    extends Omit<
        React.TextareaHTMLAttributes<HTMLTextAreaElement>,
        "name" | "defaultValue"
    > {
    label: string;
    name: string;
    rows?: number;
    placeholder?: string;
    defaultValue?: string;
    validate?: (value: string) => boolean;
    validationMessage?: string;
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
    label,
    name,
    rows = 5,
    placeholder,
    defaultValue = "",
    className,
    required = false,
    validate,
    validationMessage,
    ...textareaProps
}) => {
    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createTextValidator(required),
        [required, validate]
    );

    useEffect(() => {
        if (!registerFieldValidation) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asString(value)),
        });
    }, [effectiveValidator, name, registerFieldValidation, validationMessage]);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
        event
    ) => {
        clearFieldError(name);
        textareaProps.onChange?.(event);
    };

    return (
        <div className="form-field">
            <label htmlFor={name}>{label}</label>

            <span className="wpcf7-form-control-wrap" data-name={name}>
                <textarea
                    {...textareaProps}
                    id={name}
                    name={name}
                    rows={rows}
                    required={required}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    onChange={handleChange}
                    className={[
                        "input",
                        "input--textarea",
                        fieldError ? "wpcf7-not-valid" : null,
                        className,
                    ]
                        .filter(Boolean)
                        .join(" ")}
                />

                {fieldError ? (
                    <span className="wpcf7-not-valid-tip" aria-hidden="true">
                        {fieldError}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

